import { css } from 'styled-components';

const menuItemIndicatorColor = ({
  theme,
}: {
  theme: { color: { brightTurquoise: string } };
}) => theme.color.brightTurquoise;

const SPACING_STEP = 4;

/** Rounds up to the nearest 4px - never floors spacing below legacy values. */
export const ceilSidebarSpace = (px: number): number =>
  Math.ceil(px / SPACING_STEP) * SPACING_STEP;

const toPx = (px: number) => `${px}px`;

/** 4px spacing scale for sidebar menu layout. */
export const SIDEBAR_SPACE_4 = toPx(4);
export const SIDEBAR_SPACE_8 = toPx(8);
export const SIDEBAR_SPACE_12 = toPx(12);
export const SIDEBAR_SPACE_16 = toPx(16);
export const SIDEBAR_SPACE_20 = toPx(20);
export const SIDEBAR_SPACE_24 = toPx(24);
export const SIDEBAR_SPACE_32 = toPx(32);

/** Legacy baselines before the token pass (community + profile sidebars). */
const LEGACY_PADDING_Y = 10;
const LEGACY_PADDING_X = 16;
const LEGACY_ROW_GAP = 10;

/** Gap from row edge → indicator, indicator → icon, and right-edge → controls. */
export const SIDEBAR_MENU_EDGE_GAP = toPx(
  ceilSidebarSpace((LEGACY_PADDING_X - SPACING_STEP) / 2),
);

/** Gap between menu icon and label. */
export const SIDEBAR_MENU_ROW_GAP = toPx(ceilSidebarSpace(LEGACY_ROW_GAP));

/** Vertical / horizontal highlight bar thickness. */
export const SIDEBAR_MENU_INDICATOR_WIDTH = SIDEBAR_SPACE_4;

/** Vertical inset for the highlight bar within a row. */
export const SIDEBAR_MENU_INDICATOR_VERTICAL_INSET = SIDEBAR_MENU_EDGE_GAP;

/** Left offset of the vertical indicator (equals edge gap). */
export const COMMUNITY_SIDEBAR_INDICATOR_LEFT = SIDEBAR_MENU_EDGE_GAP;

/**
 * Horizontal row padding: edge gap + indicator + edge gap (8 + 4 + 8 = 20).
 * Ceiled from legacy 16px left inset.
 */
export const COMMUNITY_SIDEBAR_MENU_ITEM_PADDING_X = toPx(
  ceilSidebarSpace(parseInt(SIDEBAR_MENU_EDGE_GAP, 10)) * 2 + SPACING_STEP,
);

export const COMMUNITY_SIDEBAR_MENU_ITEM_PADDING_Y = toPx(
  ceilSidebarSpace(LEGACY_PADDING_Y),
);

export const COMMUNITY_SIDEBAR_MENU_ITEM_PADDING = `${COMMUNITY_SIDEBAR_MENU_ITEM_PADDING_Y} ${COMMUNITY_SIDEBAR_MENU_ITEM_PADDING_X}`;

/** Extra inset for nested list labels (icon slot + icon-to-label gap). */
export const COMMUNITY_SIDEBAR_NESTED_LIST_INDENT = `calc(${SIDEBAR_SPACE_12} + ${SIDEBAR_MENU_ROW_GAP})`;

/** Shared aliases for profile + community sidebars. */
export const SIDEBAR_MENU_ITEM_PADDING = COMMUNITY_SIDEBAR_MENU_ITEM_PADDING;
export const SIDEBAR_MENU_ITEM_PADDING_X =
  COMMUNITY_SIDEBAR_MENU_ITEM_PADDING_X;
export const SIDEBAR_MENU_INDICATOR_LEFT = COMMUNITY_SIDEBAR_INDICATOR_LEFT;

/** Contained + button size in the community sidebar (ceil to 4px grid). */
export const COMMUNITY_SIDEBAR_PLUS_BUTTON_SIZE = toPx(ceilSidebarSpace(28));

/** Right inset for + buttons and collapse arrows - matches row padding. */
export const COMMUNITY_SIDEBAR_PLUS_BUTTON_RIGHT =
  COMMUNITY_SIDEBAR_MENU_ITEM_PADDING_X;

/** Right inset for count badges when a + button is present (padding + button + gap). */
export const COMMUNITY_SIDEBAR_BADGE_RIGHT_WITH_PLUS = `calc(${COMMUNITY_SIDEBAR_MENU_ITEM_PADDING_X} + ${COMMUNITY_SIDEBAR_PLUS_BUTTON_SIZE} + ${SIDEBAR_MENU_EDGE_GAP})`;

/** Right inset for profile sidebar count badges. */
export const SIDEBAR_MENU_BADGE_RIGHT = COMMUNITY_SIDEBAR_MENU_ITEM_PADDING_X;

/** Legacy tab metrics before the 4px token pass (ceil → 4px grid). */
const LEGACY_TAB_PILL_PADDING = 10;
const LEGACY_TAB_PILL_PADDING_Y_MOBILE = 2;
const LEGACY_TAB_PILL_PADDING_X_MOBILE = 10;
const LEGACY_TAB_LIST_PADDING_Y = 8;
const LEGACY_TAB_LIST_PADDING_X = 10;
const LEGACY_TAB_ITEM_SPACING = 20;
const LEGACY_TAB_ITEM_SPACING_LEGACY = 25;
const LEGACY_TAB_BLOCK_PADDING_LEFT = 23;
const LEGACY_TAB_BLOCK_PADDING_RIGHT = 20;
const LEGACY_TAB_BLOCK_MARGIN_BOTTOM = 10;
const LEGACY_TAB_BLOCK_GAP_ROW = 12;
const LEGACY_TAB_BLOCK_GAP_COL = 16;
const LEGACY_TAB_INDICATOR_GAP = 6;
const LEGACY_TAB_LIST_MIN_HEIGHT = 67;
const LEGACY_TAB_ITEM_SPACING_MOBILE = 12;
const LEGACY_TAB_BLOCK_PADDING_MOBILE = 12;
const LEGACY_TAB_LIST_PADDING_Y_MOBILE = 4;
const LEGACY_TAB_LIST_PADDING_X_MOBILE = 8;

/** Space between scheduler tab pill and its bottom indicator bar. */
export const SCHEDULER_TAB_INDICATOR_GAP = toPx(
  ceilSidebarSpace(LEGACY_TAB_INDICATOR_GAP),
);

/** Horizontal inset for the scheduler tab bottom indicator. */
export const SCHEDULER_TAB_INDICATOR_INSET = toPx(
  ceilSidebarSpace(LEGACY_TAB_INDICATOR_GAP),
);

/** Scheduler / navigation tab bar - legacy spacing on a 4px grid. */
export const SCHEDULER_TAB_PILL_PADDING = toPx(
  ceilSidebarSpace(LEGACY_TAB_PILL_PADDING),
);
export const SCHEDULER_TAB_PILL_PADDING_MOBILE = `${toPx(
  ceilSidebarSpace(LEGACY_TAB_PILL_PADDING_Y_MOBILE),
)} ${toPx(ceilSidebarSpace(LEGACY_TAB_PILL_PADDING_X_MOBILE))}`;
export const SCHEDULER_TAB_LIST_PADDING = `${toPx(
  ceilSidebarSpace(LEGACY_TAB_LIST_PADDING_Y),
)} ${toPx(ceilSidebarSpace(LEGACY_TAB_LIST_PADDING_X))}`;
export const SCHEDULER_TAB_ITEM_SPACING = toPx(
  ceilSidebarSpace(LEGACY_TAB_ITEM_SPACING),
);
export const SCHEDULER_TAB_ITEM_SPACING_LEGACY = toPx(
  ceilSidebarSpace(LEGACY_TAB_ITEM_SPACING_LEGACY),
);
export const SCHEDULER_TAB_LIST_MIN_HEIGHT = toPx(
  ceilSidebarSpace(LEGACY_TAB_LIST_MIN_HEIGHT),
);
export const SCHEDULER_TAB_BLOCK_PADDING_LEFT = toPx(
  ceilSidebarSpace(LEGACY_TAB_BLOCK_PADDING_LEFT),
);
export const SCHEDULER_TAB_BLOCK_PADDING_RIGHT = toPx(
  ceilSidebarSpace(LEGACY_TAB_BLOCK_PADDING_RIGHT),
);
export const SCHEDULER_TAB_BLOCK_GAP = `${toPx(
  ceilSidebarSpace(LEGACY_TAB_BLOCK_GAP_ROW),
)} ${toPx(ceilSidebarSpace(LEGACY_TAB_BLOCK_GAP_COL))}`;
export const SCHEDULER_TAB_BLOCK_MARGIN_BOTTOM = toPx(
  ceilSidebarSpace(LEGACY_TAB_BLOCK_MARGIN_BOTTOM),
);

/** Mobile (< smPlus) tab metrics - legacy values on the 4px grid. */
export const SCHEDULER_TAB_ITEM_SPACING_MOBILE = toPx(
  ceilSidebarSpace(LEGACY_TAB_ITEM_SPACING_MOBILE),
);
export const SCHEDULER_TAB_BLOCK_PADDING_MOBILE = toPx(
  ceilSidebarSpace(LEGACY_TAB_BLOCK_PADDING_MOBILE),
);
export const SCHEDULER_TAB_LIST_PADDING_MOBILE = `${toPx(
  ceilSidebarSpace(LEGACY_TAB_LIST_PADDING_Y_MOBILE),
)} ${toPx(ceilSidebarSpace(LEGACY_TAB_LIST_PADDING_X_MOBILE))}`;

/** @deprecated Use margin via `SCHEDULER_TAB_ITEM_SPACING` on tab items. */
export const SCHEDULER_TAB_LIST_GAP = SCHEDULER_TAB_ITEM_SPACING;

type SidebarMenuListItemHighlightOptions = {
  indicatorLeft?: string;
  includeActive?: boolean;
  includeFocusVisible?: boolean;
};

export const sidebarMenuListItemHighlight = ({
  indicatorLeft = SIDEBAR_MENU_EDGE_GAP,
  includeActive = true,
  includeFocusVisible = true,
}: SidebarMenuListItemHighlightOptions = {}) => {
  const activeSelector = includeActive ? ', &.active' : '';
  const focusSelector = includeFocusVisible ? ', &:focus-visible' : '';

  return css`
    position: relative;
    transition: background 0.15s ease, color 0.15s ease;

    &::before {
      content: '';
      position: absolute;
      left: ${indicatorLeft};
      top: 50%;
      width: ${SIDEBAR_MENU_INDICATOR_WIDTH};
      height: calc(100% - ${SIDEBAR_MENU_INDICATOR_VERTICAL_INSET} * 2);
      border-radius: 2px;
      background-color: ${menuItemIndicatorColor};
      transform: translateY(-50%) scaleY(0);
      transform-origin: center;
      transition: transform 0.15s ease;
      pointer-events: none;
    }

    &:hover${activeSelector}${focusSelector} {
      background-color: ${({ theme }) => theme.color.blackSqueeze};

      &::before {
        transform: translateY(-50%) scaleY(1);
      }
    }
  `;
};

/** Animated left bar when `::before` is already used (e.g. online status dot). */
export const sidebarMenuListItemHighlightAfter = ({
  indicatorLeft = SIDEBAR_MENU_EDGE_GAP,
  includeActive = false,
}: SidebarMenuListItemHighlightOptions = {}) => {
  const activeSelector = includeActive ? ', &.active' : '';

  return css`
    position: relative;
    transition: background 0.15s ease, color 0.15s ease;

    &::after {
      content: '';
      position: absolute;
      left: ${indicatorLeft};
      top: 50%;
      width: ${SIDEBAR_MENU_INDICATOR_WIDTH};
      height: calc(100% - ${SIDEBAR_MENU_INDICATOR_VERTICAL_INSET} * 2);
      border-radius: 2px;
      background-color: ${menuItemIndicatorColor};
      transform: translateY(-50%) scaleY(0);
      transform-origin: center;
      transition: transform 0.15s ease;
      pointer-events: none;
    }

    &:hover${activeSelector} {
      background-color: ${({ theme }) => theme.color.blackSqueeze};

      &::after {
        transform: translateY(-50%) scaleY(1);
      }
    }
  `;
};

/** Bottom bar for scheduler tabs - horizontal counterpart to the sidebar vertical indicator. */
export const schedulerTabBottomIndicatorStyles = css`
  display: block;
  width: calc(100% - ${SCHEDULER_TAB_INDICATOR_INSET} * 2);
  height: ${SIDEBAR_MENU_INDICATOR_WIDTH};
  margin-top: ${SCHEDULER_TAB_INDICATOR_GAP};
  border-radius: 2px;
  background-color: ${menuItemIndicatorColor};
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.15s ease;
  flex-shrink: 0;
  pointer-events: none;
`;
